import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Edit,
  Settings,
  Users,
  Loader
} from 'lucide-react';
import api from '../../api';

const LibraryRulesModal = ({ isOpen, onClose, tenantId, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // State for rules - expanded with all fields
  const [rules, setRules] = useState({
    student: {
      maxBooksAllowed: 5,
      defaultIssueDays: 14,
      finePerDay: 5,
      maxFineCap: 500,
      gracePeriodDays: 2,
      maxRenewals: 2,
      renewalDays: 7
    },
    staff: {
      maxBooksAllowed: 10,
      defaultIssueDays: 21,
      finePerDay: 5,
      maxFineCap: 1000,
      gracePeriodDays: 3,
      maxRenewals: 3,
      renewalDays: 10
    },
    faculty: {
      maxBooksAllowed: 15,
      defaultIssueDays: 30,
      finePerDay: 5,
      maxFineCap: 1500,
      gracePeriodDays: 5,
      maxRenewals: 4,
      renewalDays: 14
    }
  });

  // State for editing
  const [editingRole, setEditingRole] = useState(null);
  const [editData, setEditData] = useState({});

  // Role config for display
  const roleConfig = {
    student: {
      label: 'Student',
      icon: Users,
      color: 'blue',
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-700'
    },
    staff: {
      label: 'Staff',
      icon: Users,
      color: 'purple',
      bg: 'bg-purple-50 dark:bg-purple-900/20',
      border: 'border-purple-200 dark:border-purple-700'
    },
    faculty: {
      label: 'Faculty',
      icon: Users,
      color: 'green',
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-700'
    }
  };

  // Fetch rules
  const fetchRules = useCallback(async () => {
    if (!tenantId) return;

    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/api/library/rules/${tenantId}`);
      
      if (response.data.status === 'success') {
        const data = response.data.data.librarySettings;
        const rulesData = {};
        
        // Extract rules for all roles
        data.rules.forEach(rule => {
          rulesData[rule.role] = {
            maxBooksAllowed: rule.maxBooksAllowed || 5,
            defaultIssueDays: rule.defaultIssueDays || 14,
            finePerDay: rule.finePerDay || 5,
            maxFineCap: rule.maxFineCap || 500,
            gracePeriodDays: rule.gracePeriodDays || 2,
            maxRenewals: rule.maxRenewals || 2,
            renewalDays: rule.renewalDays || 7
          };
        });
        
        setRules(rulesData);
      }
    } catch (err) {
      console.error('Error fetching rules:', err);
      setError('Failed to load rules. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Fetch rules on modal open
  useEffect(() => {
    if (isOpen && tenantId) {
      fetchRules();
    }
  }, [isOpen, tenantId, fetchRules]);

  // Update role rules
  const handleUpdateRole = async (role) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.put(
        `/api/library/rules/${tenantId}/role/${role}`,
        editData
      );
      
      if (response.data.status === 'success') {
        // Update local state
        setRules(prev => ({
          ...prev,
          [role]: editData
        }));
        
        setSuccess(`${roleConfig[role]?.label || role} rules updated successfully`);
        setEditingRole(null);
        setEditData({});
        
        if (onSave) onSave();
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update rules');
    } finally {
      setSaving(false);
    }
  };

  // Start editing
  const startEditing = (role) => {
    setEditingRole(role);
    setEditData(rules[role] || {
      maxBooksAllowed: 5,
      defaultIssueDays: 14,
      finePerDay: 5,
      maxFineCap: 500,
      gracePeriodDays: 2,
      maxRenewals: 2,
      renewalDays: 7
    });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingRole(null);
    setEditData({});
    setError(null);
  };

  // Render rule field
  const renderRuleField = (label, key, value, type = 'number', options = {}) => {
    const isEditing = editingRole && editData[key] !== undefined;
    const currentValue = isEditing ? editData[key] : value;

    return (
      <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
        <label className="text-sm text-gray-600 dark:text-gray-300">
          {label}
        </label>
        {isEditing ? (
          <input
            type={type}
            value={currentValue}
            onChange={(e) => {
              const val = type === 'number' ? parseFloat(e.target.value) : e.target.value;
              setEditData(prev => ({ ...prev, [key]: val }));
            }}
            className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            min={options.min || 0}
            max={options.max}
            step={options.step || 1}
          />
        ) : (
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {value !== undefined ? value : '-'}
          </span>
        )}
      </div>
    );
  };

  // Render role card
  const renderRoleCard = (role) => {
    const config = roleConfig[role];
    if (!config) return null;

    const roleRules = rules[role] || {
      maxBooksAllowed: 5,
      defaultIssueDays: 14,
      finePerDay: 5,
      maxFineCap: 500,
      gracePeriodDays: 2,
      maxRenewals: 2,
      renewalDays: 7
    };
    
    const isEditing = editingRole === role;

    return (
      <div 
        key={role}
        className={`border rounded-xl overflow-hidden transition-all duration-300 ${
          config.border
        } shadow-sm hover:shadow-md`}
      >
        {/* Role Header */}
        <div className={`flex items-center justify-between p-4 ${config.bg} border-b border-gray-200 dark:border-gray-700`}>
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
              <config.icon className={`h-5 w-5 text-${config.color}-600 dark:text-${config.color}-400`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{config.label}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Max: {roleRules.maxBooksAllowed} books • {roleRules.defaultIssueDays} days
              </p>
            </div>
          </div>
        </div>

        {/* Role Details */}
        <div className="p-4 bg-white dark:bg-gray-800">
          {isEditing ? (
            // Edit Mode
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  {renderRuleField('Max Books Allowed', 'maxBooksAllowed', roleRules.maxBooksAllowed, 'number', { min: 0 })}
                </div>
                <div className="col-span-2">
                  {renderRuleField('Issue Duration (Days)', 'defaultIssueDays', roleRules.defaultIssueDays, 'number', { min: 1 })}
                </div>
                <div className="col-span-2">
                  {renderRuleField('Fine Per Day (₹)', 'finePerDay', roleRules.finePerDay, 'number', { min: 0 })}
                </div>
                <div className="col-span-2">
                  {renderRuleField('Max Fine Cap (₹)', 'maxFineCap', roleRules.maxFineCap, 'number', { min: 0 })}
                </div>
                <div className="col-span-2">
                  {renderRuleField('Grace Period (Days)', 'gracePeriodDays', roleRules.gracePeriodDays, 'number', { min: 0 })}
                </div>
                <div className="col-span-2">
                  {renderRuleField('Max Renewals', 'maxRenewals', roleRules.maxRenewals, 'number', { min: 0 })}
                </div>
                <div className="col-span-2">
                  {renderRuleField('Renewal Days', 'renewalDays', roleRules.renewalDays, 'number', { min: 0 })}
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={cancelEditing}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateRole(role)}
                  disabled={saving}
                  className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                >
                  {saving ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  <span>Save Changes</span>
                </button>
              </div>
            </div>
          ) : (
            // View Mode
            <div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                <div className="p-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Max Books</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{roleRules.maxBooksAllowed}</p>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Issue Days</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{roleRules.defaultIssueDays}</p>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Fine/Day</p>
                  <p className="text-lg font-bold text-yellow-600 dark:text-yellow-400">₹{roleRules.finePerDay}</p>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Max Fine</p>
                  <p className="text-lg font-bold text-red-600 dark:text-red-400">₹{roleRules.maxFineCap}</p>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Grace Period</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{roleRules.gracePeriodDays} days</p>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Max Renewals</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{roleRules.maxRenewals}</p>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Renewal Days</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{roleRules.renewalDays} days</p>
                </div>
              </div>

              <button
                onClick={() => startEditing(role)}
                className="w-full py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <Edit className="h-4 w-4" />
                <span>Edit {config.label} Rules</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                <Settings className="h-6 w-6 mr-2 text-blue-600 dark:text-blue-400" />
                Library Rules
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Manage borrowing rules, fines, and renewals
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={fetchRules}
                disabled={loading}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Loading */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader className="h-8 w-8 text-blue-600 animate-spin" />
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading rules...</span>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-lg flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Success */}
            {success && (
              <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-lg flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-700 dark:text-green-400">{success}</p>
              </div>
            )}

            {/* Rules Cards */}
            {!loading && (
              <div className="space-y-4">
                {Object.keys(roleConfig).map(role => renderRoleCard(role))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Changes are saved immediately
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LibraryRulesModal;